import type { Face } from './faces';
import type { HoleFit } from './holeFit';

// THE BORES A TABLE IS POINTING AT, WHERE THE VIEWER CAN DRAW THEM.
//
// A hole is five numbers and a word, and every one of them is a claim about a place on the
// part — which is a thing to LOOK at rather than to read back. So the holes table publishes
// the rows somebody has switched on and the viewport draws them: a cross where each bore goes
// and a ring the width of it round that. Nothing is cut until a Drill is pressed.
//
// The table is under the phase's 3D card and the viewport is inside it, so a prop would thread
// through two components that have no interest in it. Same arrangement as orientAnswers, where
// that argument is written out, and as shownMesh, which carries the answer back the other way.
//
// ONE MESH AT A TIME, AND SO ONE VALUE — not a value per mesh, which is what this was.
//
// Keyed by job id, the table and the viewer each looked their answer up under an id they had
// been handed separately, and a preview that never moved is exactly what two ids that do not
// match look like: one end publishes under 41, the other reads under 41 or under nothing at
// all, and neither can tell which. There is one part open and it is the mesh on screen, so the
// id was carrying no information and could only be wrong.
//
// NOT PERSISTED. It is a mark on a picture while somebody is reading a table, and it goes when
// the table does — what survives is the edit, on the mesh's own history.
export type HoleMark = {
  face: Face;
  // Millimetres across that face from its bottom-left corner, seen from outside — the app's
  // one way of naming a place on a part (see partFaces, where the convention lives).
  u: number;
  v: number;
  // Millimetres. Zero until somebody has typed one, and then the mark is a bare cross: where
  // the hole goes is answerable before how wide it is, and a preview that waited for both
  // would be blank through the half of the form that decides WHERE.
  diameter: number;
  // HOW FAR IN IT GOES, in millimetres, so the mark can be the bore and not just its mouth.
  // A ring on a face says a hole is there and says nothing about the half of a hole that
  // matters when two parts have to meet in it — whether it stops 10 mm down or comes out the
  // far side. Null while nobody has said, and the mark is its mouth alone.
  depth?: number | null;
  // Whether it comes out the other side. Drawn to the far face when it does, since a through
  // hole's whole point is that it does not stop.
  through?: boolean;
  // AND WHICH ROW OF THE TABLE THIS ONE IS, as the colour that row wears. Chosen by the table
  // and carried here rather than decided here — the colour belongs to a row and the viewer has
  // never seen the table. Four holes marked in one red answer "where are they" and not "which
  // is which", and on a rail with two pairs at the same width the second question is the one
  // being asked. Absent on the drill form's own preview, which is a single hole nobody is
  // comparing and keeps the app's red for "about to change".
  colour?: number;
  // AND HOW FAR UNDER THE BOX'S PLANE THE WOOD BEGINS THERE, in millimetres, when the reading
  // that found this hole already said. The viewer casts its own ring of rays for it otherwise
  // — which is the only thing it can do for the drill form, where the hole does not exist yet
  // — and that second measurement is against the mesh as it is NOW. A row is a reading of the
  // mesh as it WAS, so after any edit the two are answers to different questions, and the mark
  // wanders off the row that drew it.
  surface?: number | null;
  // AND THE BALL A FILL WOULD CUT HERE, across, in millimetres. Not a bore and not a reading:
  // it is what step two of a fill is ABOUT to take out — one sphere at each mouth — drawn
  // before it is taken so the size of the bite can be argued with while it is still a picture.
  // Always the app's red, whatever colour the row it came from wears: red is what this app
  // means by "about to change", and this is the only mark in the list that is about the
  // future rather than about the mesh.
  ball?: number;
  // THE WOUND BORDERS A CUT LEFT, one flat array of segment endpoints per ring, in the file's
  // own frame. Not a reading and not a plan: these are the rings a fill has ALREADY opened,
  // and the ones a close will be handed. Drawn a hue apart each, because they are closed one
  // at a time and the question they answer is whether the one you are looking at is there.
  rings?: number[][];
  // AND THE HARDWARE THAT GOES IN IT, slid in once and left there — drawn instead of the ring
  // and the cross, which would sit over it. See holeFit.
  fit?: HoleFit;
  // WHICH PART OF THE SCENE IT IS IN, by instance — where a viewer holds several (a joint, see
  // JointHoles). Absent, it is on every part, which is the one-part viewer's only case.
  on?: string;
};

// A LIST AND NOT A MARK, because a reading is compared. One hole answers "is it where the
// drawing says"; four answered together say whether they are in line, evenly spaced, and on
// the faces they should be — which is the question somebody actually has about a part, and
// it cannot be asked one mark at a time.
let shown: HoleMark[] = [];

// AND THE VALUE WITHOUT REACT, for the viewport.
//
// The marks follow a table being pressed, and the way they kept NOT doing that was a chain of
// four links — a store write, a subscription, a re-render, an effect — any one of which
// failing looks identical from the outside: a ring that stands still while the rows under it
// change. The scene already runs a frame loop that reads live state; handed this, it reads the
// marks there too, and there is no longer a chain that can go quiet. See mountAssembly's
// `holeMarks`.
export const currentHoleMarks = (): HoleMark[] => shown;

export const setHoleMarks = (marks: HoleMark[]): void => { shown = marks; };

// AND NOTHING LEFT STANDING when the table that drew them goes: a bore on a part that nothing
// on the page accounts for reads as a hole that is already there.
export const clearDrill = (): void => { shown = []; };
